import { useEffect, useRef, useState } from 'react';
import type { HistoryResponse } from '@shared/protocol';
import { api } from '../../lib/api';
import { getPalette, useCanvas } from '../../lib/canvas';
import type { LoudnessTarget } from '../../lib/meterMath';
import type { LiveRefs } from '../../lib/useStreamLive';
import { Panel, Segmented } from '../Panel';

const MIN = -50;
const MAX = -5;

interface Series {
  t: number[];
  m: (number | null)[];
  s: (number | null)[];
}

export function LoudnessHistory({ streamId, live, target }: { streamId: number; live: React.RefObject<LiveRefs>; target: LoudnessTarget }) {
  const [minutes, setMinutes] = useState(5);
  const series = useRef<Series>({ t: [], m: [], s: [] });
  const lastFrameT = useRef(0);

  useEffect(() => {
    let cancelled = false;
    series.current = { t: [], m: [], s: [] };
    api<HistoryResponse>('GET', `/api/streams/${streamId}/history?minutes=${minutes}`)
      .then((h) => {
        if (cancelled) return;
        const t = h.momentary.map((_, i) => h.start + (i + 1) * h.step);
        // Keep anything appended live while the request was in flight.
        const cur = series.current;
        series.current = { t: [...t, ...cur.t], m: [...h.momentary, ...cur.m], s: [...h.shortTerm, ...cur.s] };
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [streamId, minutes]);

  const ref = useCanvas((ctx, w, h) => {
    const p = getPalette();
    const f = live.current?.frame;
    const s = series.current;
    if (f && f.t !== lastFrameT.current) {
      lastFrameT.current = f.t;
      s.t.push(f.t);
      const g = f.groups.find((x) => x.main) ?? f.groups[0];
      s.m.push(g?.loudness.momentary ?? null);
      s.s.push(g?.loudness.shortTerm ?? null);
    }
    const now = f?.t ?? Date.now();
    const span = minutes * 60_000;
    const start = now - span;
    // Trim old points (keep one before the window for a continuous line).
    let drop = 0;
    while (drop < s.t.length - 1 && s.t[drop + 1] < start) drop++;
    if (drop > 0) {
      s.t.splice(0, drop);
      s.m.splice(0, drop);
      s.s.splice(0, drop);
    }

    const left = 40;
    const right = w - 8;
    const top = 8;
    const bottom = h - 22;
    const xOf = (t: number) => left + ((t - start) / span) * (right - left);
    const yOf = (db: number) => bottom - ((Math.max(MIN, Math.min(MAX, db)) - MIN) / (MAX - MIN)) * (bottom - top);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = p.scopeBg;
    ctx.fillRect(left, top, right - left, bottom - top);

    // Tolerance band and target line
    ctx.fillStyle = p.graphBand;
    const y0 = yOf(target.integrated + target.tolerance);
    const y1 = yOf(target.integrated - target.tolerance);
    ctx.fillRect(left, y0, right - left, y1 - y0);
    ctx.strokeStyle = p.graphTarget;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(left, Math.round(yOf(target.integrated)) + 0.5);
    ctx.lineTo(right, Math.round(yOf(target.integrated)) + 0.5);
    ctx.stroke();
    ctx.setLineDash([]);

    // Grid and labels
    ctx.font = `11px ${p.font}`;
    ctx.fillStyle = p.scale;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.strokeStyle = p.scopeGrid;
    for (let db = -50; db <= -5; db += 5) {
      const y = Math.round(yOf(db)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
      ctx.stroke();
      if (db % 10 === 0 || db === -5) ctx.fillText(String(db), left - 6, y);
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const tickEvery = minutes <= 1 ? 10_000 : minutes <= 5 ? 60_000 : minutes <= 15 ? 180_000 : 600_000;
    for (let t = Math.ceil(start / tickEvery) * tickEvery; t <= now; t += tickEvery) {
      const x = Math.round(xOf(t)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.stroke();
      const ago = Math.round((now - t) / 1000);
      const label = ago === 0 ? 'now' : ago < 60 ? `-${ago}s` : `-${Math.round(ago / 60)}m`;
      ctx.fillText(label, x, bottom + 5);
    }

    const line = (values: (number | null)[], color: string, width: number) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.beginPath();
      let pen = false;
      for (let i = 0; i < values.length; i++) {
        const v = values[i];
        if (v === null || v < MIN - 10) {
          pen = false;
          continue;
        }
        const x = xOf(s.t[i]);
        const y = yOf(v);
        if (pen) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
        pen = true;
      }
      ctx.stroke();
      ctx.lineWidth = 1;
    };
    line(s.m, p.graphMomentary, 1);
    line(s.s, p.graphShort, 2);
  });

  return (
    <Panel
      title="Loudness history (main programme)"
      actions={
        <Segmented<number>
          label="History range"
          value={minutes}
          onChange={setMinutes}
          options={[1, 5, 15, 60].map((m) => ({ value: m, label: `${m} min` }))}
        />
      }
    >
      <canvas ref={ref} className="history-canvas" role="img" aria-label={`Loudness over the last ${minutes} minutes`} />
      <div className="legend">
        <span>
          <i className="swatch" style={{ background: 'var(--graph-short)' }} /> Short-term
        </span>
        <span>
          <i className="swatch" style={{ background: 'var(--graph-momentary)' }} /> Momentary
        </span>
        <span>
          <i className="swatch" style={{ background: 'var(--graph-band)', outline: '1px solid var(--graph-target)' }} /> Target{' '}
          {target.integrated} LUFS, plus or minus {target.tolerance} LU
        </span>
      </div>
    </Panel>
  );
}
